import fetchLoyaltyActionRewards from 'actions/loyalty/fetchLoyaltyActionRewards'
import { BUSINESS_DETAILS_REGIONS } from 'constants/config/region'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import {
  isSupportedLoyaltyActionReward,
  SupportedActionReward,
  type SupportedLoyaltyActionReward,
} from 'loyalty/constants/loyaltyActionRewards'
import useLuxPlusSubscriptionProduct from 'luxPlus/hooks/useLuxPlusSubscriptionProduct'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getHasPaidLuxPlusMembershipInCurrentRegion } from 'luxPlus/selectors/membership'
import { useEffect, useMemo } from 'react'

const EMPTY_PENDING_ACTION_NAMES: ReadonlySet<SupportedActionReward> = new Set()

interface ActionRewardVisibilityContext {
  state: App.State
  isComplete: boolean
  /**
   * Actions that are explicitly being polled for completion.
   * Slow to settle quests like SubscribeToCommunications would disappear and re-appear otherwise.
   */
  pendingActionNames: ReadonlySet<SupportedActionReward>
}

interface UseLoyaltyActionRewardsOptions {
  pendingActionNames?: ReadonlySet<SupportedActionReward>
}

/** FE-specific additional guards on top of BE eligibility flag */
const ACTION_REWARD_CAN_SHOW: Partial<Record<SupportedActionReward, (context: ActionRewardVisibilityContext) => boolean>> = {
  // [SupportedActionReward.LUX_PLUS_RENEWAL]: ({ state }) => getHasPaidLuxPlusMembershipInCurrentRegion(state),
  [SupportedActionReward.LUX_PLUS_BACKUP_PAYMENT_METHOD_ADDED]: ({ state }) => getHasPaidLuxPlusMembershipInCurrentRegion(state),
  [SupportedActionReward.BUSINESS_DETAILS_ADDED]: ({ state, isComplete }) => (
    isComplete ||
    BUSINESS_DETAILS_REGIONS.some(region => region === state.geo.currentRegionCode)
  ),
  [SupportedActionReward.SUBSCRIBE_TO_COMMUNICATIONS]: ({ state, isComplete, pendingActionNames }) => (
    isComplete ||
    (
      !!state.leSubscriptions.emailSubscribedUpdatedAt &&
      (
        !state.leSubscriptions.settings.emailSubscribed ||
        pendingActionNames.has(SupportedActionReward.SUBSCRIBE_TO_COMMUNICATIONS)
      )
    )
  ),
  [SupportedActionReward.DATE_OF_BIRTH_ADDED]: ({ state, isComplete }) => isComplete || !state.auth.account.dob,
  [SupportedActionReward.PHONE_NUMBER_ADDED]: ({ state, isComplete }) => isComplete || !(state.auth.account.phone && state.auth.account.phonePrefix),
}

function useLoyaltyActionRewards({ pendingActionNames = EMPTY_PENDING_ACTION_NAMES }: UseLoyaltyActionRewardsOptions = {}) {
  const dispatch = useAppDispatch()
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const subscriptionOffers = useAppSelector(state => state.luxPlus.products.subscription.offers)
  const subscriptionError = useAppSelector(state => state.luxPlus.products.subscription.error)

  // Hidden dependency: fetchLoyaltyActionRewards does not load the LuxPlus
  // catalog, but LuxPlusRenewal earn metadata reads it from Redux
  // (getLoyaltyActionRewardEarnRequest). Wait until offers/error are present.
  const subscriptionCatalogReady = !isLuxPlusEnabled || !!subscriptionOffers || !!subscriptionError

  const actionRewards = useAppSelector(state => state.loyalty.actionRewards)
  const rewards = useAppSelector((state): Array<SupportedLoyaltyActionReward> => {
    const data = state.loyalty.actionRewards.data
    if (!data) return EmptyArray

    const supportedRewards = data.filter(isSupportedLoyaltyActionReward)

    return supportedRewards.filter((reward) => {
      return ACTION_REWARD_CAN_SHOW[reward.actionName]?.({
        state,
        isComplete: reward.isComplete,
        pendingActionNames,
      }) ?? true
    })
  }, shallowEqual)

  useLuxPlusSubscriptionProduct(isLuxPlusEnabled)

  useEffect(() => {
    if (!subscriptionCatalogReady) return
    dispatch(fetchLoyaltyActionRewards({ force: true }))
  }, [dispatch, subscriptionCatalogReady])

  // Keep existing data during refetch so the section does not flash the loading skeleton.
  const isInitialLoading = !actionRewards.data && (
    !subscriptionCatalogReady || !!actionRewards.fetching || !!actionRewards.initial
  )

  return useMemo(() => ({
    rewards,
    isInitialLoading,
  }), [isInitialLoading, rewards])
}

export default useLoyaltyActionRewards
